"use client"

import Link from "next/link"
import { usePathname } from "next/navigation"
import { useEffect, useRef, useState } from "react"
import { ArrowUpRight, ChevronDown, Menu, X } from "lucide-react"

import { cn } from "@/lib/utils"
import { applyHref, PROGRAM_TAG_BY_PATH } from "@/lib/booking"
import { ColdApproachWordmark } from "@/components/marketing/cold-approach-wordmark"
import { MarketingLink, marketingIconActionClass } from "@/components/marketing/marketing-action"

// The nav sells the coaching offerings, grouped into TWO categories (owner,
// 2026-07-21: "inside coaching we have the three-month flagship mentorship, the
// bootcamp, and the one-on-one coaching calls. And inside done for you, we have
// the online dating management and AI photos") — supersedes the earlier
// no-dropdowns / five-flat-links rule (newest wins). Each category is a
// dropdown; every offer page stays one click away. Articles, Courses, and Sign
// in live in the footer; applying happens only on /apply. The online
// done-for-you flagship is "Online Dating Concierge" at
// /online-dating-concierge; /profile-review and /done-for-you-online-dating
// both redirect there (next.config.ts).
type NavItem = { href: string; label: string }
// Each category's button navigates to its FLAGSHIP by default on click (owner,
// 2026-07-21: "if they click the coaching it by default goes to the three month
// mentorship") while hover opens the dropdown of all its offers.
const NAV_CATEGORIES: readonly { label: string; flagship: string; items: readonly NavItem[] }[] = [
  {
    label: "Coaching",
    flagship: "/mentorship",
    items: [
      { href: "/mentorship", label: "Mentorship Program" },
      { href: "/bootcamp", label: "Bootcamp" },
      { href: "/calls", label: "1-on-1 Calls" },
      { href: "/online-dating-mentorship", label: "Online Dating Mentorship" },
    ],
  },
  {
    label: "Done For You",
    flagship: "/online-dating-concierge",
    items: [
      { href: "/online-dating-concierge", label: "Online Dating Concierge" },
      { href: "/profile-makeover", label: "Profile Makeover" },
      { href: "/ai-photos", label: "AI Photos" },
    ],
  },
]

function isActiveSection(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`)
}

export function Masthead() {
  const [open, setOpen] = useState(false)
  const headerRef = useRef<HTMLElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)
  // Which desktop category dropdown is open (by label), or null. Opens on hover
  // or click; closes on mouse-leave, outside-click, Escape, or route change.
  const [openMenu, setOpenMenu] = useState<string | null>(null)
  const pathname = usePathname()
  // Contextual offering-interest tag: the global Apply button carries the tag
  // of the offering page the visitor is currently reading, so leads started
  // from the masthead still record which offering drew them (see lib/booking).
  const applyTarget = applyHref(pathname ? PROGRAM_TAG_BY_PATH[pathname] : undefined)
  // Close the mobile drawer when the route changes. Done during render (React's
  // "reset state when a value changes" habit) instead of in an effect, so it
  // never triggers a cascading post-commit re-render.
  const [prevPathname, setPrevPathname] = useState(pathname)
  if (pathname !== prevPathname) {
    setPrevPathname(pathname)
    setOpen(false)
    setOpenMenu(null)
  }
  // The transparent float is OFF everywhere, including the landing page.
  //
  // It existed for one reason: the old landing hero was a photograph that bled
  // up behind the header, so the bar went transparent with white nav text and a
  // dark scrim underneath to keep that text legible. The approved landing
  // rebuild replaced that photo hero with a white one, which left the scrim
  // painting a grey wash down over white paper (owner: "there's this really
  // weird fade right now").
  //
  // With no dark hero to float over, the precondition for the whole treatment
  // is gone, so the bar is the crisp solid paper bar from the very top. This is
  // a consequence of the approved hero, not a header redesign.
  const overlay = false

  // Escape closes the mobile drawer + any open desktop dropdown.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Focus inside the closing phone menu goes back to its toggle, not the page.
        if (document.activeElement?.closest("#masthead-mobile-nav")) headerRef.current?.querySelector<HTMLElement>('[aria-controls="masthead-mobile-nav"]')?.focus()
        setOpen(false)
        setOpenMenu(null)
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [])

  // Close an open desktop dropdown when clicking anywhere outside the header
  // (covers a click-opened menu; hover-opened menus also close on mouse-leave).
  useEffect(() => {
    if (!openMenu) return
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target
      if (target instanceof Node && headerRef.current?.contains(target)) return
      setOpenMenu(null)
    }
    document.addEventListener("pointerdown", onPointerDown)
    return () => document.removeEventListener("pointerdown", onPointerDown)
  }, [openMenu])

  // The phone menu closes only when he taps its X, presses Escape, follows a
  // link in it, or leaves the page any other way (any navigation to another
  // page closes it, through the route-change reset above). His order:
  // "someone might accidentally Start scrolling when they mean to click So you
  // should force somebody actually hit the X before closing the hamburger".
  // So nothing listens for a swipe, a wheel or trackpad turn, a scroll key, a
  // scroll event, or a tap outside the header while it is open. A swipe that
  // starts on the page below scrolls the page under the sticky menu; one that
  // starts inside the menu stays in the menu (overscroll containment on its
  // list), so after the X he is where he was.

  // While the menu is open the header sits one layer above the routing bubble
  // (routing-bubble.tsx, z-index 40, from 640px up), which would otherwise
  // cover the bottom of the menu on a phone held sideways. Below 640px there
  // is no bubble, and the sticky bar keeps painting over the header as before.
  return (
    <header ref={headerRef} className={cn("sticky top-0 z-40", open && "sm:z-[41]")}>
      {/* Background layers. The bar itself is transparent; these absolute layers
          carry the look and the content row below sits above them via `relative`.
          A crisp SOLID paper bar that is transparent ONLY at the very top of the
          landing hero (white nav text floats over the image) and solid the
          instant you scroll past it. No translucent glass wash over the hero
          (owner: "this fading sucks"). */}
      <div
        aria-hidden
        className={cn(
          "absolute inset-0 transition-opacity duration-150 ease-out",
          overlay ? "opacity-0" : "opacity-100"
        )}
      >
        <div className="glass-chrome h-full w-full" />
      </div>
      <div className="relative mx-auto flex h-16 max-w-[92rem] items-center justify-between gap-3 px-5 py-3 sm:px-8 lg:px-10">
        <Link
          href="/"
          aria-label="Cold Approach home"
          className={cn(
            "group flex h-11 shrink-0 items-center transition-opacity hover:opacity-80",
            overlay ? "text-white" : "text-[var(--color-ink)]"
          )}
        >
          <ColdApproachWordmark size={90} decorative />
        </Link>

        <nav aria-label="Main" className="hidden items-center gap-1 xl:flex">
          {NAV_CATEGORIES.map((cat) => {
            const isMenuOpen = openMenu === cat.label
            const anyActive = cat.items.some((i) => isActiveSection(pathname, i.href))
            return (
              <div
                key={cat.label}
                className="relative"
                onMouseEnter={() => setOpenMenu(cat.label)}
                onMouseLeave={() => setOpenMenu((current) => (current === cat.label ? null : current))}
              >
                {/* The category label NAVIGATES to its flagship on click
                    (Coaching -> /mentorship) while hover opens the dropdown of
                    every offer in the category (owner, 2026-07-21). */}
                <Link
                  href={cat.flagship}
                  aria-haspopup="true"
                  aria-expanded={isMenuOpen}
                  onClick={() => setOpenMenu(null)}
                  className={cn(
                    "inline-flex min-h-10 items-center gap-1 whitespace-nowrap rounded-[7px] border border-transparent px-3 text-[0.82rem] font-semibold transition-colors",
                    overlay
                      ? cn("hover:border-white/25 hover:bg-white/10 hover:text-white", anyActive || isMenuOpen ? "text-white" : "text-white/85")
                      : cn("hover:border-[var(--color-rule)] hover:bg-[var(--color-paper-high)] hover:text-[var(--color-ink)]", anyActive || isMenuOpen ? "text-[var(--color-ink)]" : "text-[var(--color-ink-muted)]")
                  )}
                >
                  {cat.label}
                  <ChevronDown size={13} aria-hidden className={cn("transition-transform duration-150", isMenuOpen && "rotate-180")} />
                </Link>
                {isMenuOpen && (
                  // pt-2 is a hover-bridge so moving from the button to the panel
                  // doesn't cross a gap that would fire mouse-leave and close it.
                  <div className="absolute left-0 top-full z-50 pt-2">
                    <div className="min-w-[15rem] overflow-hidden rounded-[12px] border border-[var(--color-rule)] bg-[var(--color-paper)] py-1.5 shadow-[var(--glass-shadow-2)]">
                      {cat.items.map((item) => {
                        const active = isActiveSection(pathname, item.href)
                        return (
                          <Link
                            key={item.href}
                            href={item.href}
                            aria-current={active ? "page" : undefined}
                            className={cn(
                              "block whitespace-nowrap px-4 py-2.5 text-[0.9rem] font-semibold transition-colors hover:bg-[var(--color-paper-deep)]",
                              active ? "text-[var(--color-vermilion)]" : "text-[var(--color-ink)]"
                            )}
                          >
                            {item.label}
                          </Link>
                        )
                      })}
                      {/* Two-line compare entry (owner pick, 2026-07-24 mockup
                          gallery "nav E"): a quiet reason line over the crimson
                          link - gives the click a reason and reads calmer than a
                          bordered pill. */}
                      <div className="border-t border-[var(--color-rule)] px-4 pb-2.5 pt-2.5">
                        <p className="text-[0.72rem] font-semibold text-[var(--color-ink-soft)]">Not sure which fits?</p>
                        <Link
                          href="/compare"
                          className="tap-target mt-0.5 inline-flex items-center gap-1.5 whitespace-nowrap text-[0.87rem] font-bold text-[var(--color-vermilion)] transition-opacity hover:opacity-75"
                        >
                          Compare every program &rarr;
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </nav>

        <div className="flex items-center gap-3">
          {/* The standalone /free-training page was retired 2026-07-17 (its
              hero-story video folded into the /apply VSL; free course is
              footer-only). The masthead now shows only Apply (owner: remove the
              Free training button). */}
          <MarketingLink
            href={applyTarget}
            size="nav"
            variant="accent"
            className="hidden whitespace-nowrap sm:inline-flex"
          >
            Find your program
            <ArrowUpRight size={14} aria-hidden />
          </MarketingLink>
          <button
            className={marketingIconActionClass({ className: cn("xl:hidden", overlay && "text-white"), variant: "ghost", size: "lg" })}
            onClick={() => {
              // A menu scrolled down its list and closed reopens at its top.
              if (!open && listRef.current) listRef.current.scrollTop = 0
              setOpen((current) => !current)
            }}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="masthead-mobile-nav"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* inert while closed: the links stay mounted for the open animation,
          but a closed drawer is invisible, so they must leave the Tab order
          and the accessibility tree with it. Nothing on screen changes. */}
      <div
        id="masthead-mobile-nav"
        aria-hidden={!open}
        inert={!open}
        className={cn(
          "relative z-10 grid overflow-hidden bg-[var(--color-paper)] transition-[grid-template-rows,opacity,transform,border-color] duration-200 ease-out xl:hidden",
          open
            ? "grid-rows-[1fr] border-t border-[var(--color-rule)] opacity-100 shadow-[var(--glass-shadow-2)] translate-y-0"
            : "pointer-events-none grid-rows-[0fr] border-t border-transparent opacity-0 -translate-y-1"
        )}
      >
        <div className="flex min-h-0 flex-col overflow-hidden">
          {/* The list scrolls inside itself when the menu is taller than the
              screen, so every link stays reachable with the menu open: capped
              at the screen under the 4rem bar and the drawer's 1px top rule,
              and on a phone also above the 4rem-and-1px sticky "Find your
              program" bar (mobile-sticky-cta.tsx), which paints over this
              header. A swipe that starts in it never scrolls the page behind.
              Its padding lives here rather than on the wrapper so focus rings
              and the link tap zones stay inside it and a menu that fits never
              turns scrollable. Nothing on screen changes when it fits. */}
          <div
            ref={listRef}
            className={cn(
              "max-h-[calc(100dvh_-_8rem_-_2px_-_env(safe-area-inset-bottom))] shrink-0 overflow-y-auto overscroll-y-contain px-2 transition-[padding] duration-200 ease-out sm:max-h-[calc(100dvh_-_4rem_-_1px)]",
              open ? "py-3" : "py-0"
            )}
          >
            {/* Two categories (Coaching / Done For You), each with its offer rows
                under an uppercase group label. The last category drops its bottom
                border so it doesn't double up with the footer's top border. */}
            {NAV_CATEGORIES.map((cat, ci) => {
              const isLastCat = ci === NAV_CATEGORIES.length - 1
              return (
                <div key={cat.label} className={cn("border-b border-[var(--color-rule)] pb-1", isLastCat && "border-b-0")}>
                  <p className="px-4 pb-1 pt-3 text-[0.72rem] font-semibold uppercase tracking-[0.12em] text-[var(--color-ink-soft)]">
                    {cat.label}
                  </p>
                  {cat.items.map((item) => {
                    const active = isActiveSection(pathname, item.href)
                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        onClick={() => setOpen(false)}
                        aria-current={active ? "page" : undefined}
                        className={cn(
                          "block rounded-[8px] px-4 py-2.5 text-[1rem] font-semibold transition-colors hover:bg-[var(--color-paper-deep)]",
                          active ? "text-[var(--color-vermilion)]" : "text-[var(--color-ink)]"
                        )}
                      >
                        {item.label}
                      </Link>
                    )
                  })}
                </div>
              )
            })}
            <div className="mt-2 border-t border-[var(--color-rule)] px-4 pt-3">
              <p className="text-[0.78rem] font-semibold text-[var(--color-ink-soft)]">Not sure which fits?</p>
              <Link
                href="/compare"
                onClick={() => setOpen(false)}
                className="tap-target mt-0.5 inline-flex items-center gap-1.5 whitespace-nowrap text-[1rem] font-bold text-[var(--color-vermilion)]"
              >
                Compare every program &rarr;
              </Link>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3 border-t border-[var(--color-rule)] px-2 pt-4 sm:hidden">
              <MarketingLink
                href={applyTarget}
                onClick={() => setOpen(false)}
                size="md"
                variant="accent"
              >
                Find your program
              </MarketingLink>
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}
